import React, { useState, useEffect } from 'react'; // Import React and hooks
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoon,
  faSun,
  faBars,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'; // Added faBars and faTimes
import styled from 'styled-components';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { useTheme } from '../../providers/ThemeProvider';
import { ReactComponent as Logo } from 'assets/vector_ikonkar.svg'; // Corrected import
import { scaleAnimation } from '../../utils/animations';
import LanguageSwitcher from '../LanguageSwitcher'; // Import LanguageSwitcher

const Nav = styled.nav`
  background: ${(props) => props.theme.colors.surface}; /* Updated */
  max-height: 60px; /* Keep existing height for now, can be adjusted */
  min-height: 60px; /* Keep existing height for now, can be adjusted */
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px; /* Increased padding */
  border-bottom: 1px solid ${(props) => props.theme.colors.borders}; /* Subtle bottom border */
  position: relative; /* For absolute positioning of mobile menu */
  z-index: 100; /* Ensure header is above other content */
  @media (max-width: 768px) {
    padding: 0 15px; /* Slightly reduce padding on mobile */
  }
`;

const NavLinksContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px; /* Spacing between individual links/icons */

  a,
  button {
    /* Target both Link components and ThemeToggleButton */
    font-family: ${(props) => props.theme.fontFamilies.primary};
    font-size: ${(props) => props.theme.fontSizes.body}; /* e.g., 1.125rem */
    font-weight: 500;
    color: ${(props) => props.theme.colors.textSecondary};
    text-decoration: none;
    padding: 5px; /* Add some padding for better click area */
    border-radius: ${(props) =>
      props.theme.borderRadius}; /* Consistent border radius */
    transition: color 0.2s ease-in-out, transform 0.2s ease-in-out,
      background-color 0.2s ease-in-out;

    &:hover {
      color: ${(props) => props.theme.colors.primary};
      transform: translateY(-1px);
      /* Optional: background-color: ${(props) =>
        props.theme.colors.surface}; for a subtle button like effect */
    }
  }

  /* Specific styling for FontAwesome icons if they are direct children of Link in NavLinksContainer (not NavLinksTitles) */
  a > svg {
    display: block; /* Helps with alignment and sizing if needed */
  }

  @media (max-width: 768px) {
    display: none; /* Hide by default on mobile, will be shown in mobile menu */
  }
`;

// NavLinksTitles will now just be a NavLinksContainer.
// The distinction for text-shadow and font-size increase on hover is removed for consistency.
const NavLinksTitles = styled(NavLinksContainer)``; // Will be hidden on mobile by NavLinksContainer style
const NavLinksIcons = styled(NavLinksContainer)``; // Will be hidden on mobile by NavLinksContainer style

const MobileMenuIcon = styled.button`
  display: none; /* Hidden by default */
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.5rem; /* Adjust size as needed */
  cursor: pointer;
  padding: 5px;
  z-index: 105; /* Above other header elements if needed */

  @media (max-width: 768px) {
    display: block; /* Show on mobile */
  }
`;

interface MobileNavLinksProps {
  $isOpen: boolean;
}

const MobileNavLinks = styled.div<MobileNavLinksProps>`
  display: none; /* Hidden by default */
  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
    flex-direction: column;
    align-items: center;
    position: absolute;
    top: 60px; /* Below the header */
    left: 0;
    right: 0;
    background: ${(props) => props.theme.colors.surface};
    border-bottom: 1px solid ${(props) => props.theme.colors.borders};
    padding: 1rem 0;
    gap: 1rem; /* Spacing between links in mobile menu */
    z-index: 99; /* Below header but above page content */
  }
`;

const StyledLogo = styled(Logo)`
  width: 45px; /* Slightly smaller */
  height: 45px; /* Slightly smaller */
  color: ${(props) => props.theme.colors.primary}; /* Updated */
  animation: ${scaleAnimation} 2s ease-in-out infinite; /* Kept animation for now */
  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

// ThemeToggleButton is now styled by NavLinksContainer's button targetting
const ThemeToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  /* All styling is now inherited from NavLinksContainer button styles */
  /* Remove specific font-size, color, padding if they are covered by NavLinksContainer */
  margin-left: 0; /* Reset margin if NavLinksContainer gap handles it */
  @media (max-width: 768px) {
    /* Ensure it's styled appropriately if shown in mobile menu */
    color: ${(props) => props.theme.colors.textSecondary};
    &:hover {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

// No explicit props are defined for Header in its current usage
interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation(); // Initialize useTranslation
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu on window resize if screen becomes larger
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Nav>
      <StyledLogo />
      <NavLinksTitles>
        {' '}
        {/* For text links */}
        <Link to="/">{t('header.home')}</Link>
        <Link to="/gallery">{t('header.gallery')}</Link>
        <Link to="/educational">{t('header.educational')}</Link>
        <Link to="/about">{t('header.about')}</Link>
        <Link to="/donations">{t('header.donations')}</Link>
        <Link to="/langar-calendar">Book Langar</Link>
      </NavLinksTitles>
      <NavLinksIcons>
        {' '}
        {/* For icon links and theme toggle */}
        <Link to="/calendar" title={t('header.events') as string}>
          <FontAwesomeIcon icon="calendar-alt" />
        </Link>
        <Link to="/programs" title={t('header.programs') as string}>
          <FontAwesomeIcon icon="earth-americas" />
        </Link>
        <Link to="/contact" title={t('header.contact') as string}>
          <FontAwesomeIcon icon="address-card" />
        </Link>
        <LanguageSwitcher />
        <ThemeToggleButton
          onClick={toggleTheme}
          title={
            theme === 'light'
              ? (t('header.switchToDarkMode') as string)
              : (t('header.switchToLightMode') as string)
          }
        >
          <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
        </ThemeToggleButton>
      </NavLinksIcons>{' '}
      {/* This group will be hidden on mobile by NavLinksContainer style */}
      <MobileMenuIcon
        onClick={toggleMobileMenu}
        aria-label={t('header.toggleNavigation') as string}
      >
        <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
      </MobileMenuIcon>
      <MobileNavLinks $isOpen={isMobileMenuOpen} className="mobile-menu">
        {' '}
        {/* Added className for context styling */}
        <Link to="/" onClick={toggleMobileMenu}>
          {t('header.home')}
        </Link>
        <Link to="/gallery" onClick={toggleMobileMenu}>
          {t('header.gallery')}
        </Link>
        <Link to="/educational" onClick={toggleMobileMenu}>
          {t('header.educational')}
        </Link>
        <Link to="/about" onClick={toggleMobileMenu}>
          {t('header.about')}
        </Link>
        <Link to="/donations" onClick={toggleMobileMenu}>
          {t('header.donations')}
        </Link>
        <Link to="/langar-calendar" onClick={toggleMobileMenu}>
          Book Langar
        </Link>
        <Link
          to="/calendar"
          title={t('header.events') as string}
          onClick={toggleMobileMenu}
        >
          <FontAwesomeIcon icon="calendar-alt" /> {t('header.events')}
        </Link>
        <Link
          to="/programs"
          title={t('header.programs') as string}
          onClick={toggleMobileMenu}
        >
          <FontAwesomeIcon icon="earth-americas" /> {t('header.programs')}
        </Link>
        <Link
          to="/contact"
          title={t('header.contact') as string}
          onClick={toggleMobileMenu}
        >
          <FontAwesomeIcon icon="address-card" /> {t('header.contact')}
        </Link>
        <LanguageSwitcher />
        <ThemeToggleButton
          onClick={() => {
            toggleTheme();
            toggleMobileMenu();
          }}
          title={
            theme === 'light'
              ? (t('header.switchToDarkMode') as string)
              : (t('header.switchToLightMode') as string)
          }
        >
          <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />{' '}
          {theme === 'light' ? t('header.darkMode') : t('header.lightMode')}
        </ThemeToggleButton>
      </MobileNavLinks>
    </Nav>
  );
};

export default Header;
